import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, TrendingUp, Users, Calendar } from 'lucide-react';
import { useRaces } from '@/hooks/useRaces';
import { useRegistrations } from '@/hooks/useRegistrations';
import { usePosts } from '@/hooks/usePosts';
import { useAllDiscounts } from '@/hooks/useDiscounts';
import { useAuth } from '@/contexts/AuthContext';
import { StatsCard, EventSummary } from './cmsdashboard.props';
import CMSDashboardView from './cmsdashboard.view';

const formatMKD = (amount: number) => {
  return amount.toLocaleString('mk-MK') + ' МКД';
};

const CMSDashboard = () => {
  const { isAdmin, isOrganizer } = useAuth();
  const navigate = useNavigate();
  const { data: races = [], isLoading: racesLoading } = useRaces();
  const { data: registrations = [], isLoading: regsLoading } = useRegistrations();
  const { data: posts = [], isLoading: postsLoading } = usePosts();
  const { data: discounts = [], isLoading: discountsLoading } = useAllDiscounts();

  const loading = racesLoading || regsLoading || postsLoading || discountsLoading;

  useEffect(() => {
    document.title = 'DrimRun | CMS Dashboard';
    return () => {
      document.title = 'DrimRun';
    };
  }, []);

  const raceTypeMap = useMemo(() => {
    const map = new Map<number, { price: number; raceId: string }>();
    races.forEach(race => {
      if (race.raceTypes) {
        race.raceTypes.forEach(rt => {
          map.set(rt.id, { price: rt.price || 0, raceId: race.id });
        });
      }
    });
    return map;
  }, [races]);

  const discountMap = useMemo(() => {
    const map = new Map<string, any>();
    discounts.forEach((d: any) => {
      const key = `${d.code}__${d.raceId}`;
      map.set(key, d);
    });
    return map;
  }, [discounts]);

  const getDiscountedPrice = useCallback((price: number, discountCode: string | null, raceId: string) => {
    if (!discountCode) return price;
    const key = `${discountCode}__${raceId}`;
    const discount = discountMap.get(key);
    if (!discount) return price;
    if (discount.discountAmount > 0) return Math.max(0, price - discount.discountAmount);
    if (discount.discountPercentage > 0) return Math.max(0, price - Math.round(price * discount.discountPercentage / 100));
    return price;
  }, [discountMap]);

  const stats = useMemo(() => {
    const now = new Date();
    const upcomingRaces = races.filter(race => new Date(race.startDate) > now);
    const confirmedRegs = registrations.filter(r => r.registrationStatus === 'Confirmed');
    const paidRegs = registrations.filter(r => r.paymentStatus === 'Paid');

    let grossRevenue = 0;
    let netRevenue = 0;

    paidRegs.forEach(r => {
      const rt = raceTypeMap.get(r.raceTypeId);
      if (!rt) return;
      grossRevenue += rt.price;
      netRevenue += getDiscountedPrice(rt.price, r.discountCode, rt.raceId);
    });

    return {
      totalEvents: races.length,
      upcomingEventsCount: upcomingRaces.length,
      totalRegistrations: registrations.length,
      confirmedRegistrations: confirmedRegs.length,
      paidRegistrations: paidRegs.length,
      grossRevenue,
      netRevenue,
      totalPosts: posts.length
    };
  }, [races, registrations, posts, raceTypeMap, getDiscountedPrice]);

  const eventSummaries: EventSummary[] = useMemo(() => {
    return races
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
      .map(race => {
        const raceRegs = registrations.filter(r => r.raceId === race.id);
        const paidRegs = raceRegs.filter(r => r.paymentStatus === 'Paid');

        let grossRevenue = 0;
        let netRevenue = 0;
        paidRegs.forEach(r => {
          const rt = raceTypeMap.get(r.raceTypeId);
          if (!rt) return;
          grossRevenue += rt.price;
          netRevenue += getDiscountedPrice(rt.price, r.discountCode, rt.raceId);
        });

        return {
          id: race.id,
          name: race.name,
          date: race.startDate,
          totalRegistrations: raceRegs.length,
          paidRegistrations: paidRegs.length,
          grossRevenue,
          netRevenue,
        };
      });
  }, [races, registrations, raceTypeMap, getDiscountedPrice]);

  const recentRegistrations = useMemo(() => registrations.slice(0, 5), [registrations]);

  const statsCards: StatsCard[] = [
    { title: 'Вкупно настани', value: stats.totalEvents.toString(), change: `${stats.upcomingEventsCount} предстојни`, icon: Calendar, color: 'bg-blue-500', path: '/cms/events' },
    { title: 'Вкупно регистрации', value: stats.totalRegistrations.toString(), change: `${stats.confirmedRegistrations} потврдени`, icon: Users, color: 'bg-green-500', path: '/cms/registrations' },
    { title: 'Приход', value: formatMKD(stats.netRevenue), change: `Без попусти: ${formatMKD(stats.grossRevenue)}`, icon: TrendingUp, color: 'bg-purple-500', path: '/cms/registrations' },
    { title: 'Објавени постови', value: stats.totalPosts.toString(), change: '', icon: BarChart3, color: 'bg-orange-500', path: '/cms/posts' }
  ];

  return (
    <CMSDashboardView
      isAdmin={isAdmin}
      loading={loading}
      statsCards={statsCards}
      recentRegistrations={recentRegistrations}
      eventSummaries={eventSummaries}
      onNavigate={navigate}
    />
  );
};

export default CMSDashboard;
